import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { SiteDataProvider } from './contexts/SiteDataContext';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import Projects from './pages/Projects';
import Sectors from './pages/Sectors';
import { TeamList, TeamProfile } from './pages/Team';
import Careers from './pages/Careers';
import Contact from './pages/Contact';
import Admin from './pages/Admin';
import News from './pages/News';
import { Toaster } from './components/ui/toaster';
import EnvironmentInfo from './components/EnvironmentInfo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Analytics tracking hook
const useAnalytics = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Don't track admin pages
    if (location.pathname.startsWith('/admin')) return;
    
    // Generate or retrieve session ID
    let sessionId = sessionStorage.getItem('analytics_session');
    if (!sessionId) {
      sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      sessionStorage.setItem('analytics_session', sessionId);
    }
    
    // Track page view
    const trackPageView = async () => {
      try {
        await fetch(`${API_URL}/api/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: location.pathname,
            referrer: document.referrer || null,
            userAgent: navigator.userAgent,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            sessionId: sessionId
          })
        });
      } catch (err) {
        // Silently fail - don't affect user experience
        console.debug('Analytics tracking failed:', err);
      }
    };
    
    // Track clicks for heatmap
    const trackClick = async (event) => {
      const target = event.target;
      const clickableElements = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
      const isClickable = clickableElements.includes(target.tagName) || 
                          target.closest('a, button, [role="button"], [onclick]');
      
      // Only track clicks on interactive elements
      if (!isClickable) return;
      
      // Get element info
      const elementType = target.tagName.toLowerCase();
      const elementText = target.innerText?.trim()?.substring(0, 100) || 
                         target.getAttribute('aria-label') ||
                         target.getAttribute('title') || '';
      
      // Calculate position as percentage of viewport
      const x = Math.round((event.clientX / window.innerWidth) * 100);
      const y = Math.round((event.clientY / window.innerHeight) * 100);
      
      try {
        await fetch(`${API_URL}/api/analytics/click`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: location.pathname,
            x,
            y,
            elementType,
            elementText,
            sessionId
          })
        });
      } catch (err) {
        console.debug('Click tracking failed:', err);
      }
    };
    
    // Add click listener
    document.addEventListener('click', trackClick);
    
    // Small delay to ensure page has loaded
    const timer = setTimeout(trackPageView, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', trackClick);
    };
  }, [location.pathname]);
};

// Analytics wrapper component
const AnalyticsTracker = ({ children }) => {
  useAnalytics();
  return children;
};

// Scroll to top on route change - proper implementation
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  // Scroll detection for smooth hover effects
  useEffect(() => {
    let scrollTimeout;
    
    const handleScroll = () => {
      // Add scrolling class
      document.body.classList.add('is-scrolling');
      
      // Clear previous timeout
      clearTimeout(scrollTimeout);
      
      // Remove class after scrolling stops (150ms delay)
      scrollTimeout = setTimeout(() => {
        document.body.classList.remove('is-scrolling');
      }, 150);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
      document.body.classList.remove('is-scrolling');
    };
  }, []);
  
  useEffect(() => {
    // Skip scroll reset for admin/CMS routes
    if (pathname.startsWith('/admin')) return;
    
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Temporarily disable smooth scroll CSS to ensure instant scroll
    const html = document.documentElement;
    const originalScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    
    // Scroll to top - multiple methods to ensure it works
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; // For Safari
    
    // Restore scroll behavior after a brief delay
    requestAnimationFrame(() => {
      html.style.scrollBehavior = originalScrollBehavior;
    });
  }, [pathname]);
  
  return null;
};

// Layout wrapper that conditionally shows Navbar/Footer
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  return (
    <AnalyticsTracker>
      <ScrollToTop />
      {!isAdminRoute && <Navbar />}
      {children}
      {!isAdminRoute && <Footer />}
      <Toaster />
      {!isAdminRoute && <EnvironmentInfo />}
    </AnalyticsTracker>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SiteDataProvider>
          <div className="App">
            <BrowserRouter>
              <AppLayout>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/:id" element={<ServiceDetail />} />
                  <Route path="/sectors" element={<Sectors />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/team" element={<TeamList />} />
                  <Route path="/team/:memberId" element={<TeamProfile />} />
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/news" element={<News />} />
                  <Route path="/admin" element={<Admin />} />
                </Routes>
              </AppLayout>
            </BrowserRouter>
          </div>
        </SiteDataProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
